import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren
} from '@angular/core';
import {NgClass, NgTemplateOutlet} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {ActivatedRoute, ParamMap, RouterLink} from '@angular/router';
import {MetaTitle} from '../../../../decorator/meta-title';
import {DateRangeSelectorComponent} from '../../../layout/date-range-selector/date-range-selector.component';
import {StatisticsService} from '../../../../services/statistics/statistics.service';
import {LoaderService} from '../../../../services/loader/loader.service';
import {UserActivityService} from '../../../../services/user-activity/user-activity.service';
import {AvailableDomainService} from '../../../../services/cms/available-domain.service';
import {AvailableDomain} from '../../../../models/cms/available-domain';
import {ProductService} from '../../../../services/product/product.service';
import {OfferService} from '../../../../services/offer/offer.service';
import {PartnerService} from '../../../../services/partner/partner.service';
import {Product} from '../../../../models/product';
import {Offer} from '../../../../models/offer';
import {Partner} from '../../../../models/partner/partner';
import {DropdownSelectComponent} from '../../../layout/dropdown-select/dropdown-select.component';
import {DropdownSelectOptionComponent} from '../../../layout/dropdown-select-option/dropdown-select-option.component';
import {ElementLoaderDirective} from '../../../../directives/element-loader/element-loader.directive';
import {StatsFilterUrlService, StatsPeriodParam} from '../../../../services/stats-filter-url/stats-filter-url.service';
import {TooltipDirective} from '../../../../directives/tooltip/tooltip.directive';

export interface FunnelStatsArrivals {
  total: number;
  ok: number;
  ar: number;
  ko: number;
  p0: number;
  none: number;
}

export interface FunnelStatsPage {
  id: number;
  name: string;
  slug: string | null;
  type: string;
  url: string | null;
  staticPageId: number | null;
  registerLead: boolean;
  level: number;
  start: boolean;
  terminal: boolean;
  reachable: boolean;
  acceptRate: number | null;
  arrivals: FunnelStatsArrivals;
  direct: number;
  views: number;
  visitors: number;
  clicks: number;
  leadP0: number;
  leadTotal: number;
  leadOk: number;
  leadKo: number;
  leadAr: number;
  leadXsell: number;
}

export interface FunnelStatsLink {
  id: number;
  fromId: number;
  toId: number;
  visitors: number;
  arrivals: FunnelStatsArrivals;
  rate: number | null;
  conditions: any[];
}

export interface FunnelStatsOutcomes {
  visitors: number;
  clicks: number;
  converted: number;
  refusedAr: number;
  refusedKo: number;
  prospectOnly: number;
  idle: number;
  multiOk: number;
  convertedStopped: number;
  leadP0: number;
  leadTotal: number;
  leadOk: number;
  leadKo: number;
  leadAr: number;
  leadXsell: number;
  acceptRate: number | null;
  conversionRate: number | null;
}

export interface FunnelStats extends FunnelStatsOutcomes {
  id: number;
  name: string;
  slug: string | null;
  enabled: boolean;
  domain: {id: number; domain: string} | null;
  startPageId: number | null;
  rank: number | null;
  levels: number[][];
  pages: FunnelStatsPage[];
  links: FunnelStatsLink[];
}

export interface FunnelStatsTotals extends FunnelStatsOutcomes {
  funnels: number;
}

interface FunnelStatsResult {
  period: {start: string; end: string};
  minRankedVisitors: number;
  totals: FunnelStatsTotals;
  funnels: FunnelStats[];
}

interface Segment {
  key: string;
  label: string;
  count: number;
  width: string;
  title: string;
}

interface FlowArrow {
  key: string;
  d: string;
  width: number;
  backward: boolean;
  labelX: number;
  labelY: number;
  label: string;
  title: string;
}

interface FlowLayout {
  width: number;
  height: number;
  arrows: FlowArrow[];
}

interface FunnelView {
  funnel: FunnelStats;
  levels: FunnelStatsPage[][];
  tone: Tone;
}

type Tone = 'good' | 'warning' | 'serious' | 'neutral';

type ArrivalStatus = 'ok' | 'p0' | 'ar' | 'ko';

const ARRIVAL_STATUSES: ArrivalStatus[] = ['ok', 'ar', 'ko', 'p0'];
const ARRIVAL_LABELS: Record<ArrivalStatus, string> = {ok: 'OK', p0: 'prospect', ar: 'AR', ko: 'KO'};

type OutcomeKey = 'converted' | 'refusedAr' | 'refusedKo' | 'prospectOnly' | 'idle';

const OUTCOMES: {key: OutcomeKey; label: string; description: string}[] = [
  {key: 'converted', label: 'Converted', description: 'sent at least one accepted lead (OK)'},
  {key: 'refusedAr', label: 'Already registered', description: 'submitted, but only got AR answers (email already in the base)'},
  {key: 'refusedKo', label: 'Refused', description: 'submitted, but only got KO answers'},
  {key: 'prospectOnly', label: 'Prospect only', description: 'left after a p0 lead, without a full submission'},
  {key: 'idle', label: 'Left without submitting', description: 'never submitted a form'},
];

type LeadKey = 'leadOk' | 'leadAr' | 'leadKo';

const LEAD_PARTS: {key: LeadKey; label: string; description: string}[] = [
  {key: 'leadOk', label: 'OK', description: 'accepted'},
  {key: 'leadAr', label: 'AR', description: 'already registered'},
  {key: 'leadKo', label: 'KO', description: 'refused'},
];

interface TrackingFilters {
  source: string;
  subid: string;
  tc1: string;
  tc2: string;
  tc3: string;
  tc4: string;
  clickid: string;
}

const TRACKING_FIELDS: (keyof TrackingFilters)[] = ['source', 'subid', 'tc1', 'tc2', 'tc3', 'tc4', 'clickid'];

const HELP = {
  visitors: 'Unique visitors (browser sessions) who opened the first page of the funnel. '
    + 'A visitor who reloads the page or comes back in the same session is counted once.',
  conversion: 'Converted visitors divided by visitors. '
    + 'A visitor is converted when at least one form they sent was accepted (OK), on any page of the funnel.',
  converted: 'Visitors who sent at least one accepted form (OK). A visitor who sent 2 OK forms is counted once.',
  outcomes: 'Every visitor is counted once, in one group only: '
    + 'Converted = at least one OK. Already registered = sent forms, only AR answers. '
    + 'Refused = sent forms, only KO answers. Prospect only = only a p0 lead. '
    + 'Left without submitting = never sent a form. The groups add up to the number of visitors.',
  submissions: 'Number of forms sent: OK (accepted) + AR (already registered) + KO (refused). '
    + 'One visitor can send several forms, for example AR first, then OK with another email.',
  acceptRate: 'OK forms divided by all forms sent (OK + AR + KO).',
  xsell: 'Number of cross-sell offers accepted. One visitor can accept several offers.',
  okSent: 'Number of accepted forms (OK). It is higher than the converted visitors '
    + 'when some visitors sent several accepted forms, usually with different emails.',
  convertedStopped: 'Converted visitors who never opened another page of this funnel after their form: '
    + 'tab closed, redirect failed, new session, or next page not tracked (external URL before the live tracking).',
  pageVisitors: 'Unique visitors (browser sessions) who opened this page. Reloads are counted once.',
  pageOrigin: 'Where the visitors of this page come from: the answer to their form on the previous page '
    + '(OK, AR, KO, p0, or no form sent). Direct = no previous funnel page in their session '
    + '(new session, shared link, bot).',
  pageSubmissions: 'Forms sent from this page and their answers: OK = accepted, AR = already registered, KO = refused. '
    + 'The percentage is OK / all forms sent from this page.',
  arrows: 'Each arrow shows the share of the visitors of a page who went to the next page. '
    + 'Hover an arrow to see the number of visitors.',
};

const DEFAULT_PERIOD = 'last-week';
const DEFAULT_MIN_LEADS = 1;
const SHOW = 10;
const RANKING_PREVIEW = 10;
const CARD_GAP = 32;

const COUNT_FORMAT = new Intl.NumberFormat('fr-FR', {maximumFractionDigits: 0});
const PERCENT_FORMAT = new Intl.NumberFormat('fr-FR', {style: 'percent', minimumFractionDigits: 1, maximumFractionDigits: 1});
const POINTS_FORMAT = new Intl.NumberFormat('fr-FR', {maximumFractionDigits: 1, signDisplay: 'always'});

@Component({
  selector: 'app-funnels',
  imports: [
    FormsModule,
    NgClass,
    NgTemplateOutlet,
    RouterLink,
    DateRangeSelectorComponent,
    DropdownSelectComponent,
    DropdownSelectOptionComponent,
    ElementLoaderDirective,
    TooltipDirective,
  ],
  templateUrl: './funnels.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './funnels.component.scss'
})
@MetaTitle('Funnel statistics')
export class FunnelsComponent implements OnInit {
  @ViewChild('periodSelector', {static: true}) periodSelector!: DateRangeSelectorComponent;
  @ViewChildren('flow') flows!: QueryList<ElementRef<HTMLElement>>;

  help = HELP;
  trackingFields = TRACKING_FIELDS;
  rankingPreview = RANKING_PREVIEW;

  initialParams: ParamMap;
  initialPeriod: StatsPeriodParam;

  domains: AvailableDomain[] = [];
  products: Product[] = [];
  offers: Offer[] = [];
  partners: Partner[] = [];
  offerLoading = false;

  domainId: number | null = null;
  search = '';
  minLeads = DEFAULT_MIN_LEADS;
  productId: number | null = null;
  offerId: number | null = null;
  partnerIds: number[] = [];
  tracking: TrackingFilters = this.createTrackingFilters();

  loading = false;
  loaded = false;
  error: string | null = null;

  period: {start: string; end: string} | null = null;
  totals: FunnelStatsTotals | null = null;
  funnels: FunnelStats[] = [];
  minRankedVisitors = 0;
  views: FunnelView[] = [];
  openedIds = new Set<number>();
  rankingExpanded = false;
  layouts: Record<number, FlowLayout> = {};
  highlightedFunnelId: number | null = null;

  private layoutTimer: any = null;

  constructor(
    private route: ActivatedRoute,
    private statisticsService: StatisticsService,
    private domainService: AvailableDomainService,
    private productService: ProductService,
    private offerService: OfferService,
    private partnerService: PartnerService,
    private loader: LoaderService,
    private userActivity: UserActivityService,
    private urlFilters: StatsFilterUrlService,
  ) {
    this.initialParams = this.urlFilters.getInitialParams(this.route);
    this.initialPeriod = this.urlFilters.getPeriod(this.initialParams, DEFAULT_PERIOD);

    const domainId = Number(this.initialParams.get('domainId'));
    this.domainId = domainId > 0 ? domainId : null;
    this.search = this.initialParams.get('search') ?? '';

    const productId = Number(this.initialParams.get('productId'));
    this.productId = productId > 0 ? productId : null;

    const offerId = Number(this.initialParams.get('offerId'));
    this.offerId = offerId > 0 ? offerId : null;

    this.partnerIds = (this.initialParams.get('partnerId') ?? '').split(',').map(Number).filter(id => id > 0);

    for (const field of TRACKING_FIELDS) {
      this.tracking[field] = this.initialParams.get(field) ?? '';
    }

    if (this.initialParams.has('minLeads')) {
      this.minLeads = Math.max(0, Number(this.initialParams.get('minLeads')) || 0);
    }
  }

  ngOnInit(): void {
    this.domainService.findAll({sort: 'domain'}).subscribe(domains => this.domains = domains);
    this.productService.findAll({sort: 'name'}).subscribe(products => this.products = products);
    this.partnerService.findAll({sort: 'companyName', order: 'asc', status: 'active'})
      .subscribe(partners => this.partners = partners);
    this.loadOffers();
  }

  changeProduct(): void {
    this.offerId = null;
    this.loadOffers();
  }

  getProductPlaceholder(): string {
    return this.products.length ? ('All ' + this.products.length + ' product(s)') : 'No product';
  }

  getOfferPlaceholder(): string {
    return this.offers.length ? ('All ' + this.offers.length + ' offer(s)') : 'No offer';
  }

  @HostListener('window:resize')
  onResize(): void {
    this.scheduleLayout();
  }

  apply(): void {
    const params = this.createParams();
    if (!params) {
      return;
    }

    this.loading = true;
    this.error = null;
    this.loader.startLoading();
    this.userActivity.reportFilters(params);
    this.urlFilters.update(this.route, this.getUrlParams());

    this.statisticsService.getFunnels(params).subscribe({
      next: (result: FunnelStatsResult) => {
        this.period = result.period;
        this.minRankedVisitors = result.minRankedVisitors;
        this.totals = result.totals;
        this.funnels = result.funnels;
        this.buildViews();
        this.loaded = true;
        this.loading = false;
        this.loader.stopLoading();
      },
      error: (error: any) => {
        this.error = error?.error?.message ?? error?.message ?? 'Funnel statistics could not be loaded.';
        this.loading = false;
        this.loader.stopLoading();
      }
    });
  }

  saveFilters(): void {
    this.urlFilters.save(this.route, this.getUrlParams());
  }

  clearFilters(): void {
    this.urlFilters.clear(this.route);
    this.urlFilters.resetPeriod(this.periodSelector, DEFAULT_PERIOD);
    this.domainId = null;
    this.search = '';
    this.minLeads = DEFAULT_MIN_LEADS;
    this.productId = null;
    this.offerId = null;
    this.partnerIds = [];
    this.tracking = this.createTrackingFilters();
    this.loadOffers();
    this.apply();
  }

  scrollTo(funnel: FunnelStats): void {
    if (!this.cardViews().some(view => view.funnel.id === funnel.id)) {
      this.openedIds.add(funnel.id);
      this.scheduleLayout();
    }

    this.highlightedFunnelId = funnel.id;
    setTimeout(() => {
      document.getElementById('funnel-stats-' + funnel.id)?.scrollIntoView({behavior: 'smooth', block: 'start'});
    });
    setTimeout(() => this.highlightedFunnelId = null, 2000);
  }

  cardViews(): FunnelView[] {
    return this.views.filter((view, index) => index < SHOW || this.openedIds.has(view.funnel.id));
  }

  rankedCount(): number {
    return this.funnels.filter(funnel => funnel.rank !== null).length;
  }

  rankingViews(): FunnelView[] {
    return this.rankingExpanded ? this.views : this.views.slice(0, RANKING_PREVIEW);
  }

  rankingHasMore(): boolean {
    return this.views.length > RANKING_PREVIEW;
  }

  toggleRanking(): void {
    this.rankingExpanded = !this.rankingExpanded;
  }

  outcomeSegments(source: FunnelStatsOutcomes): Segment[] {
    return OUTCOMES.filter(outcome => source[outcome.key] > 0).map(outcome => ({
      key: outcome.key,
      label: outcome.label,
      count: source[outcome.key],
      width: this.share(source[outcome.key], source.visitors),
      title: this.count(source[outcome.key]) + ' visitors (' + this.percent(this.ratio(source[outcome.key], source.visitors))
        + ') ' + outcome.description,
    }));
  }

  leadSegments(source: {leadTotal: number; leadOk: number; leadAr: number; leadKo: number}): Segment[] {
    return LEAD_PARTS.filter(part => source[part.key] > 0).map(part => ({
      key: part.key,
      label: part.label,
      count: source[part.key],
      width: this.share(source[part.key], source.leadTotal),
      title: this.count(source[part.key]) + ' submissions ' + part.description
        + ' (' + this.percent(this.ratio(source[part.key], source.leadTotal)) + ')',
    }));
  }

  originSummary(page: FunnelStatsPage): string {
    const parts = ARRIVAL_STATUSES.filter(status => page.arrivals[status] > 0)
      .map(status => this.count(page.arrivals[status]) + ' after ' + ARRIVAL_LABELS[status]);

    if (page.arrivals.none > 0) {
      parts.push(this.count(page.arrivals.none) + ' without form');
    }
    if (page.direct > 0) {
      parts.push(this.count(page.direct) + ' direct');
    }

    return parts.join(' · ');
  }

  hasNextPage(funnel: FunnelStats): boolean {
    return funnel.pages.some(page => page.start && !page.terminal);
  }

  arrivalsSummary(arrivals: FunnelStatsArrivals): string {
    const parts = ARRIVAL_STATUSES.filter(status => arrivals[status] > 0)
      .map(status => this.count(arrivals[status]) + ' ' + ARRIVAL_LABELS[status]);

    if (arrivals.none > 0) {
      parts.push(this.count(arrivals.none) + ' without submitting');
    }

    return parts.length ? parts.join(' · ') : 'none';
  }

  count(value: number | null | undefined): string {
    return value === null || value === undefined ? '–' : COUNT_FORMAT.format(value);
  }

  percent(value: number | null | undefined): string {
    return value === null || value === undefined ? '–' : PERCENT_FORMAT.format(value);
  }

  delta(value: number | null | undefined): string {
    if (value === null || value === undefined || !this.totals?.conversionRate) {
      return '';
    }

    return POINTS_FORMAT.format((value - this.totals.conversionRate) * 100) + ' pts';
  }

  tone(conversionRate: number | null): Tone {
    const average = this.totals?.conversionRate ?? null;
    if (conversionRate === null || average === null) {
      return 'neutral';
    }

    if (conversionRate >= average) {
      return 'good';
    }

    return conversionRate >= average / 2 ? 'warning' : 'serious';
  }

  halfAverage(): number | null {
    return this.totals?.conversionRate === null || this.totals?.conversionRate === undefined
      ? null
      : this.totals.conversionRate / 2;
  }

  meterWidth(rate: number | null): string {
    return Math.max(0, Math.min(100, (rate ?? 0) * 100)).toFixed(1) + '%';
  }

  funnelUrl(funnel: FunnelStats): string | null {
    if (!funnel.domain || !funnel.slug) {
      return null;
    }

    return 'https://' + funnel.domain.domain + '/fl/' + funnel.slug;
  }

  pageTypeLabel(page: FunnelStatsPage): string {
    if (page.start) {
      return 'Entry page';
    }

    return page.type === 'url' ? 'External URL' : 'Static page';
  }

  trackFunnel(index: number, view: FunnelView): number {
    return view.funnel.id;
  }

  private createParams(): any | null {
    const selector = this.periodSelector;
    if (!selector?.dateFromSelector || !selector?.dateToSelector) {
      this.error = 'Select a period.';
      return null;
    }

    const params: any = {
      startDate: selector.dateFromSelector + 'T00:00:00',
      endDate: selector.dateToSelector + 'T23:59:59',
    };

    if (this.domainId) {
      params.domainId = this.domainId;
    }

    if (this.search.trim()) {
      params.search = this.search.trim();
    }

    if (this.minLeads > 0) {
      params.minLeads = this.minLeads;
    }

    if (this.productId) {
      params.productId = this.productId;
    }

    if (this.offerId) {
      params.offerId = this.offerId;
    }

    if (this.partnerIds.length) {
      params.partnerId = this.partnerIds.join(',');
    }

    for (const field of TRACKING_FIELDS) {
      if (this.tracking[field].trim()) {
        params[field] = this.tracking[field].trim();
      }
    }

    return params;
  }

  private ratio(value: number, total: number): number | null {
    return total > 0 ? value / total : null;
  }

  private share(value: number, total: number): string {
    return total > 0 ? (value / total * 100).toFixed(2) + '%' : '0%';
  }

  private createTrackingFilters(): TrackingFilters {
    return {source: '', subid: '', tc1: '', tc2: '', tc3: '', tc4: '', clickid: ''};
  }

  private loadOffers(): void {
    this.offerLoading = true;
    const params: any = {sort: 'name'};
    if (this.productId) {
      params.productId = this.productId;
    }

    this.offerService.findAll(params).subscribe(offers => {
      this.offers = offers;
      this.offerLoading = false;
      if (this.offerId && !offers.some(offer => offer.id === this.offerId)) {
        this.offerId = null;
      }
    });
  }

  private getUrlParams(): any {
    return {
      ...this.urlFilters.getPeriodParams(this.periodSelector),
      domainId: this.domainId,
      search: this.search.trim(),
      minLeads: this.minLeads !== DEFAULT_MIN_LEADS ? this.minLeads : null,
      productId: this.productId,
      offerId: this.offerId,
      partnerId: this.partnerIds,
      ...Object.fromEntries(TRACKING_FIELDS.map(field => [field, this.tracking[field].trim()])),
    };
  }

  private buildViews(): void {
    this.rankingExpanded = false;
    this.openedIds = new Set<number>();
    this.views = this.funnels.map(funnel => {
      const pagesById = new Map(funnel.pages.map(page => [page.id, page]));

      return {
        funnel,
        levels: funnel.levels.map(level => level.map(id => pagesById.get(id)).filter((page): page is FunnelStatsPage => !!page)),
        tone: funnel.rank === null ? 'neutral' : this.tone(funnel.conversionRate),
      };
    });

    this.layouts = {};
    this.scheduleLayout();
  }

  private scheduleLayout(): void {
    clearTimeout(this.layoutTimer);
    this.layoutTimer = setTimeout(() => this.drawArrows(), 50);
  }

  private drawArrows(): void {
    const layouts: Record<number, FlowLayout> = {};

    this.flows?.forEach(ref => {
      const container = ref.nativeElement;
      const funnelId = Number(container.dataset['funnelId']);
      const view = this.views.find(item => item.funnel.id === funnelId);
      if (!view) {
        return;
      }

      const cards = new Map<number, HTMLElement>();
      container.style.removeProperty('--card-height');
      let cardHeight = 0;
      container.querySelectorAll<HTMLElement>('[data-page-id]').forEach(card => {
        cards.set(Number(card.dataset['pageId']), card);
        cardHeight = Math.max(cardHeight, card.offsetHeight);
      });
      container.style.setProperty('--card-height', cardHeight + 'px');

      const outgoing = new Map<number, number[]>();
      const incoming = new Map<number, number[]>();
      for (const link of view.funnel.links) {
        outgoing.set(link.fromId, [...(outgoing.get(link.fromId) ?? []), link.id]);
        incoming.set(link.toId, [...(incoming.get(link.toId) ?? []), link.id]);
      }

      const arrows: FlowArrow[] = [];
      for (const link of view.funnel.links) {
        const from = cards.get(link.fromId);
        const to = cards.get(link.toId);
        if (!from || !to) {
          continue;
        }

        const outs = outgoing.get(link.fromId) ?? [];
        const ins = incoming.get(link.toId) ?? [];
        const outIndex = outs.indexOf(link.id);
        const inIndex = ins.indexOf(link.id);
        const rate = link.rate ?? 0;
        const fromName = view.funnel.pages.find(page => page.id === link.fromId)?.name ?? '';
        const label = link.rate === null ? COUNT_FORMAT.format(link.visitors) : PERCENT_FORMAT.format(link.rate);
        const title = COUNT_FORMAT.format(link.visitors) + ' visitors went from "' + fromName + '" to "'
          + (view.funnel.pages.find(page => page.id === link.toId)?.name ?? '') + '"'
          + (link.rate === null ? '' : ', i.e. ' + PERCENT_FORMAT.format(link.rate) + ' of the visitors of "' + fromName + '"')
          + '. Their result on "' + fromName + '": ' + this.arrivalsSummary(link.arrivals) + '.';
        const width = 1.5 + 4.5 * Math.max(0, Math.min(1, rate));
        const backward = to.offsetLeft < from.offsetLeft + from.offsetWidth;

        if (backward) {
          const x1 = from.offsetLeft + from.offsetWidth * (outIndex + 1) / (outs.length + 1);
          const y1 = from.offsetTop + from.offsetHeight;
          const x2 = to.offsetLeft + to.offsetWidth * (inIndex + 1) / (ins.length + 1);
          const y2 = to.offsetTop + to.offsetHeight;
          const dip = Math.max(y1, y2) + CARD_GAP;

          arrows.push({
            key: 'link-' + link.id,
            d: `M${x1},${y1} C${x1},${dip + 24} ${x2},${dip + 24} ${x2},${y2 + 4}`,
            width,
            backward: true,
            labelX: (x1 + x2) / 2,
            labelY: dip + 10,
            label,
            title,
          });
          continue;
        }

        const x1 = from.offsetLeft + from.offsetWidth;
        const y1 = from.offsetTop + from.offsetHeight * (outIndex + 1) / (outs.length + 1);
        const x2 = to.offsetLeft;
        const y2 = to.offsetTop + to.offsetHeight * (inIndex + 1) / (ins.length + 1);
        const dx = Math.max(24, (x2 - x1) / 2);

        arrows.push({
          key: 'link-' + link.id,
          d: `M${x1},${y1} C${x1 + dx},${y1} ${x2 - dx},${y2} ${x2 - 4},${y2}`,
          width,
          backward: false,
          labelX: (x1 + x2) / 2,
          labelY: (y1 + y2) / 2,
          label,
          title,
        });
      }

      layouts[funnelId] = {
        width: container.scrollWidth,
        height: container.scrollHeight,
        arrows,
      };
    });

    this.layouts = layouts;
  }
}
